import { useRef, useState, useContext } from 'react';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { NotificationContext } from 'contexts/NotificationContext';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import NotificationOutlined from '@ant-design/icons/NotificationOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { notifications, unreadCount, markAsRead } = useContext(NotificationContext);

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleNotificationClick = async (notif) => {
    setSelectedNotification(notif);
    if (!notif.is_read) {
      await markAsRead(notif.id);
    }
    setOpen(false);
  };

  const handleMarkAllRead = async () => {
    const unread = notifications.filter(n => !n.is_read);
    for (const n of unread) {
      await markAsRead(n.id);
    }
  };

  // Only show message type notifications in the bell icon dropdown
  const messageNotifs = notifications.filter(n => n.type?.toLowerCase() === 'message');

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <IconButton
          color="secondary"
          variant="light"
          sx={(theme) => ({
            color: 'text.primary',
            bgcolor: open ? 'grey.100' : 'transparent'
          })}
          aria-label="open profile"
          ref={anchorRef}
          aria-controls={open ? 'profile-grow' : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
        >
          <Badge badgeContent={unreadCount} color="primary">
            <BellOutlined />
          </Badge>
        </IconButton>
        <Popper
          placement={downMD ? 'bottom' : 'bottom-end'}
          open={open}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
        >
          {({ TransitionProps }) => (
            <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
              <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MainCard
                    title="Notifications"
                    elevation={0}
                    border={false}
                    content={false}
                    secondary={
                      <>
                        {unreadCount > 0 && (
                          <Tooltip title="Mark all as read">
                            <IconButton color="success" size="small" onClick={handleMarkAllRead}>
                              <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    }
                  >
                    <List
                      component="nav"
                      sx={{
                        p: 0,
                        maxHeight: 400,
                        overflow: 'auto',
                        '& .MuiListItemButton-root': {
                          py: 0.5,
                          px: 2,
                          '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                          '& .MuiAvatar-root': avatarSX,
                          '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                        }
                      }}
                    >
                      {messageNotifs.length === 0 ? (
                        <ListItem>
                          <ListItemText primary={<Typography variant="subtitle2" align="center">No notifications</Typography>} />
                        </ListItem>
                      ) : (
                        messageNotifs.map((notif) => (
                          <ListItem
                            key={notif.id}
                            component={ListItemButton}
                            divider
                            selected={!notif.is_read}
                            onClick={() => handleNotificationClick(notif)}
                            secondaryAction={
                              <Typography variant="caption" noWrap>
                                {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </Typography>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ 
                                color: notif.is_read ? 'secondary.main' : 'primary.main', 
                                bgcolor: notif.is_read ? 'secondary.lighter' : 'primary.lighter' 
                              }}>
                                {notif.priority > 0 ? <NotificationOutlined /> : <MessageOutlined />}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={<Typography variant="h6">{notif.title}</Typography>}
                              secondary={new Date(notif.created_at).toLocaleDateString()}
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </MainCard>
                </ClickAwayListener>
              </Paper>
            </Transitions>
          )}
        </Popper>
      </Box>

      {/* Notification Detail Modal */}
      <Dialog
        open={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        maxWidth="sm"
        fullWidth
      >
        {selectedNotification && (
          <>
            <DialogTitle>
              <Typography variant="h5">{selectedNotification.title}</Typography>
            </DialogTitle>
            <DialogContent dividers>
              <div dangerouslySetInnerHTML={{ __html: selectedNotification.message }} />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                Received on: {new Date(selectedNotification.created_at).toLocaleString()}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedNotification(null)} color="primary">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
